import { WAVE_TODO_AI_AGENTS } from "./blocks.js";
import { participantPresenceEntries } from "./wavePresence.js";
import { WaveRole } from "./types.js";
/** Mention token after `@` (no spaces). */
export function participantMentionLabel(p) {
    const name = p.displayName?.trim();
    if (name) {
        const first = name.split(/\s+/)[0];
        if (first)
            return first.replace(/[^\p{L}\p{N}._-]/gu, "");
    }
    const email = p.email?.trim();
    if (email) {
        const local = email.split("@")[0];
        if (local)
            return local.replace(/[^\w.-]/g, "");
    }
    return p.sub.slice(0, 8);
}
export function listTodoMentionCandidates(participants, query = "") {
    const q = query.trim().toLowerCase();
    const out = [];
    for (const p of participants) {
        const label = participantMentionLabel(p);
        const hay = [label, p.displayName ?? "", p.email ?? ""].join(" ").toLowerCase();
        if (!q || hay.includes(q) || label.toLowerCase().startsWith(q)) {
            out.push({
                ref: p.sub,
                label,
                subtitle: p.email ?? p.displayName ?? p.sub,
                kind: "participant",
            });
        }
    }
    for (const agent of WAVE_TODO_AI_AGENTS) {
        const hay = `${agent.label} ${agent.id}`.toLowerCase();
        if (!q || hay.includes(q) || agent.label.toLowerCase().startsWith(q)) {
            out.push({
                ref: agent.id,
                label: agent.label,
                glyph: agent.glyph,
                kind: "agent",
            });
        }
    }
    return out;
}
const MENTION_TOKEN_RE = /@[\p{L}\p{N}._-]+/gu;
/** Resolve `@Label` tokens in a todo title to assignee refs (participant subs or `agent:*`). */
export function parseTodoAssigneesFromTitle(title, participants) {
    const labelToRef = new Map();
    for (const p of participants) {
        labelToRef.set(participantMentionLabel(p).toLowerCase(), p.sub);
    }
    for (const agent of WAVE_TODO_AI_AGENTS) {
        labelToRef.set(agent.label.toLowerCase(), agent.id);
    }
    const assignees = new Set();
    const re = new RegExp(MENTION_TOKEN_RE.source, "gu");
    let match;
    while ((match = re.exec(title)) !== null) {
        const token = match[0].slice(1).toLowerCase();
        const ref = labelToRef.get(token);
        if (ref)
            assignees.add(ref);
    }
    return [...assignees];
}
/** Remove `@mention` tokens for optional plain-text export (not used for Drift mirror sync). */
export function stripTodoMentionsForPlainText(title) {
    return title.replace(MENTION_TOKEN_RE, "").replace(/\s{2,}/g, " ").trim();
}
/** Returns active `@` mention at cursor, if any. */
export function activeTodoMentionQuery(text, cursor) {
    const before = text.slice(0, cursor);
    const at = before.lastIndexOf("@");
    if (at < 0)
        return null;
    const query = before.slice(at + 1);
    if (/\s/.test(query))
        return null;
    return { start: at, query };
}
/** Wave roster for todo @mentions — same peers as the presence header (participants + online + last seen). */
export function participantRecordsForTodoMentions(args) {
    const entries = participantPresenceEntries(args.participants, args.presences, args.lastSeen, args.currentSub, args.ownerSub, { includeSelf: true, currentUserEmail: args.currentUserEmail });
    const bySub = new Map();
    for (const participant of args.participants) {
        bySub.set(participant.sub, participant);
    }
    for (const entry of entries) {
        if (bySub.has(entry.sub))
            continue;
        bySub.set(entry.sub, {
            sub: entry.sub,
            email: entry.email ?? null,
            displayName: entry.label === "Tu" ? null : entry.label,
            role: WaveRole.Viewer,
            joinedAt: new Date(0).toISOString(),
        });
    }
    return [...bySub.values()].sort((a, b) => participantMentionLabel(a).localeCompare(participantMentionLabel(b)));
}
